import { useLocation } from 'react-router-dom';
import { useEffect, useState, useCallback, useLayoutEffect, MutableRefObject, useRef } from 'react';
import useResizeObserver from '@react-hook/resize-observer';
import {
  isUserLoggedIn,
  isUserLoggedInStateInTransition,
  isUserLoggedInStateUnknown,
  isUserParentAccessStorybook,
  isUserDistrictAdministrator,
  isUserSessionExpired,
  openHostedUIForClever,
  openHostedUIForGoogle,
  openHostedUIForClassLink,
  reactivateUserSession,
  signInUser,
  qrCodeSignIn as signInWithQRCode,
} from './UserManagementHelpers';
import type { SignInRetryStatus } from './SmartDataResidencyAuth';
import { getUserFriendlyErrorMessage } from './GlobalFunctions';
import { logger } from './error-tracker';
import { isUserHasKey, isUserMissingSchool } from './GlobalFunctions';
import { getStore } from './ApplicationState';
import { setCookie } from './aaa';

export function useQuery(): URLSearchParams {
  return new URLSearchParams(useLocation().search);
}

export function useAuth() {
  return {
    isUserSessionExpired,
    isUserLoggedIn,
    isUserHasKey,
    isUserDistrictAdministrator,
    isUserMissingSchool,
    isUserLoggedInStateUnknown,
    isUserLoggedInStateInTransition,
    isUserParentAccessStorybook,
    reactivateUserSession,
    onLoggedInStateKnown: (callback: () => void) => {
      const unsubscribe = getStore().subscribe(() => {
        if (!isUserLoggedInStateInTransition()) {
          unsubscribe();
          callback();
        }
      });
    },
  };
}

export const useDebouncedEffect = (effect: () => void, deps: any[] = [], delay: number) => {
  useEffect(() => {
    const handler = setTimeout(() => effect(), delay);

    return () => clearTimeout(handler);
  }, [...deps, delay]);
};

export const useOutsideClick = (ref: React.RefObject<HTMLElement>, action: () => void) => {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as HTMLElement)) {
        action();
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);
};

export function useLogin() {
  return {
    nativeLogin: async (email: string, password: string, onRetry?: (status: SignInRetryStatus) => void) => {
      try {
        return await signInUser(email, password, onRetry);
      } catch (error) {
        if (error instanceof Error) {
          const message = getUserFriendlyErrorMessage(error);

          console.error(`Login failed: ${message}`);
          const errorToLog = new Error(`Login failed for ${email} with error: ${message}`);
          logger.logException(errorToLog);
        }
        throw error;
      }
    },
    googleSignIn: (origin: string) => {
      setCookie('peekapak.idP', 'Google');
      setCookie('peekapak.oauthSignInOrigin', origin);
      openHostedUIForGoogle();
    },
    cleverSignIn: (origin: string) => {
      setCookie('peekapak.idP', 'Clever');
      setCookie('peekapak.oauthSignInOrigin', origin);
      openHostedUIForClever();
    },
    classLinkSignIn: (origin: string) => {
      setCookie('peekapak.idP', 'ClassLink');
      setCookie('peekapak.oauthSignInOrigin', origin);
      openHostedUIForClassLink();
    },
    qrCodeSignIn: async (qrCode: string) => {
      try {
        const result = await signInWithQRCode(qrCode);
        return result;
      } catch (error) {
        if (error instanceof Error) {
          const message = getUserFriendlyErrorMessage(error);

          console.error(`Login failed: ${message}`);
          const errorToLog = new Error(`Login failed for using QR code with error: ${message}`);
          logger.logException(errorToLog);
        }
        throw error;
      }
    },
  };
}

export const useWidth = (elementRef: React.RefObject<HTMLElement>) => {
  const [width, setWidth] = useState<number | null>(null);

  const updateWidth = useCallback(() => {
    if (elementRef && elementRef.current) {
      const { width } = elementRef.current.getBoundingClientRect();
      setWidth(width);
    }
  }, [elementRef]);

  useEffect(() => {
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [updateWidth]);

  return width;
};

interface Size {
  width: number;
  height: number;
}

export function useElementSize<T extends HTMLElement = HTMLDivElement>(): [MutableRefObject<T | null>, Size] {
  const target = useRef<T | null>(null);
  const [size, setSize] = useState<Size>({
    width: 0,
    height: 0,
  });

  const setRoundedSize = ({ width, height }: Size) => {
    setSize({ width: Math.round(width), height: Math.round(height) });
  };

  useLayoutEffect(() => {
    if (target.current) {
      setRoundedSize(target.current.getBoundingClientRect());
    }
  }, [target]);

  useResizeObserver(target, (entry) => {
    const { inlineSize: width, blockSize: height } = entry.contentBoxSize[0];
    setRoundedSize({ width, height });
  });

  return [target, size];
}
